import { createMultiPartRequest, createRequest } from '../helpers/api_utils';
import {
  BankSearch,
  BankSearchResults,
  PatchBankSearch,
  ResultsOverride,
} from '../types/candi_bank_types';
import { apiUrl } from './api_url';

const apiEndpoint = apiUrl + '/candi-bank';

// ---------------------------------------------------------------------------
// POST /candi-bank/searches  (multipart/form-data)
// Exactly one of: file (PDF/DOCX), text (pasted JD), job_opening_id.
// 202 when queued, 200 when deduped (same content hash).
// ---------------------------------------------------------------------------
export async function createBankSearch(
  input: { file: File } | { text: string } | { job_opening_id: string },
): Promise<Response> {
  const formData = new FormData();

  if ('file' in input) {
    formData.append('file', input.file);
  } else if ('text' in input) {
    formData.append('text', input.text);
  } else {
    formData.append('job_opening_id', input.job_opening_id);
  }

  return fetch(
    createMultiPartRequest(
      `${apiEndpoint}/searches`,
      'POST',
      true,
      formData,
    ),
  );
}

// ---------------------------------------------------------------------------
// GET /candi-bank/searches?page=1&limit=20
// ---------------------------------------------------------------------------
export async function listBankSearches(
  page: number = 1,
  limit: number = 20,
): Promise<Response> {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  return fetch(
    createRequest(
      `${apiEndpoint}/searches?${params.toString()}`,
      'GET',
      'application/json',
      true,
    ),
  );
}

// ---------------------------------------------------------------------------
// GET /candi-bank/searches/{id}  (polling target)
// ---------------------------------------------------------------------------
export async function getBankSearch(id: string): Promise<Response> {
  return fetch(
    createRequest(
      `${apiEndpoint}/searches/${id}`,
      'GET',
      'application/json',
      true,
    ),
  );
}

// ---------------------------------------------------------------------------
// PATCH /candi-bank/searches/{id}
// JSON body (PatchBankSearch) — send only what changed.
// 202 + queued:true for query-feeding fields; 200 for the rest.
// ---------------------------------------------------------------------------
export async function patchBankSearch(
  id: string,
  body: PatchBankSearch,
): Promise<Response> {
  return fetch(
    createRequest(
      `${apiEndpoint}/searches/${id}`,
      'PATCH',
      'application/json',
      true,
      body as unknown as Object,
    ),
  );
}

// ---------------------------------------------------------------------------
// POST /candi-bank/searches/{id}/refresh
// Opening-sourced searches only. Always 202.
// ---------------------------------------------------------------------------
export async function refreshBankSearch(id: string): Promise<Response> {
  return fetch(
    createRequest(
      `${apiEndpoint}/searches/${id}/refresh`,
      'POST',
      'application/json',
      true,
    ),
  );
}

// ---------------------------------------------------------------------------
// POST /candi-bank/searches/{id}/results?page=1&limit=20&include_unverified=false
// Optional JSON body (ResultsOverride) for live re-ranking without saving.
// ---------------------------------------------------------------------------
export async function getBankSearchResults(
  id: string,
  page: number = 1,
  limit: number = 20,
  includeUnverified: boolean = false,
  override?: ResultsOverride,
): Promise<Response> {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    include_unverified: includeUnverified.toString(),
  });

  return fetch(
    createRequest(
      `${apiEndpoint}/searches/${id}/results?${params.toString()}`,
      'POST',
      'application/json',
      true,
      override as unknown as Object | undefined,
    ),
  );
}

// ---------------------------------------------------------------------------
// DELETE /candi-bank/searches/{id}
// Hard delete. Returns {"deleted": true}.
// ---------------------------------------------------------------------------
export async function deleteBankSearch(id: string): Promise<Response> {
  return fetch(
    createRequest(
      `${apiEndpoint}/searches/${id}`,
      'DELETE',
      'application/json',
      true,
    ),
  );
}
